import { TripCard } from "../../cards/TripCard"
import styles from "./styles.module.scss"
import PlusOutline from "~icons/eva/plus-outline"
import { useStores } from "../../../models/helpers/useStores"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createTrip, getUserTrips, userTripsQueryKey } from "../../../services/firestore/trips"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo } from "react"
import { Popover } from "../../atoms/Popover"
import UserAvatar from "../../atoms/UserAvatar"
import { AccountModal } from "./modals/AccountModal"
import { CreateTripFormValues, TripDetailsModal } from "../TripDetailsModal"
import { ModalWrapper } from "../../templates/ModalWrapper"
import { getTripDetails } from "@cruisen/react-shared"
import SearchOutline from "~icons/eva/search-outline"
import ChevronDownOutline from "~icons/eva/chevron-down-outline"
import ChevronUpOutline from "~icons/eva/chevron-up-outline"
import CloseOutline from "~icons/eva/close-outline"
import { FilterSortDropdown } from "./modals/FilterSortDropdown"
import { filterTrips } from "./utils/filterTrips"
import { TripNavigatorState } from "../../../models/RootStore"
import debounce from "lodash.debounce"
import classNames from "classnames"
import { SubscriptionModal } from "../SubscriptionModal"

type AvailableModals =
  | "accountModal"
  | "createTrip"
  | "filteringModal"
  | "sortingModal"
  | "subscriptionModal"

export type FilterType = "open" | "archived" | "all"
export type SortType = "priority" | "date"

class TripListStore {
  activeModal: null | AvailableModals
  isSearchInputVisible: boolean
  searchInput: string
  filterType: FilterType
  sortType: SortType

  constructor() {
    this.searchInput = ""
    this.activeModal = null
    this.isSearchInputVisible = false
    this.filterType = "open"
    this.sortType = "priority"
    makeAutoObservable(this)
  }

  setSearchInput(value: string) {
    this.searchInput = value
  }

  setIsSearchInputVisible(isVisible: boolean) {
    this.isSearchInputVisible = isVisible
  }

  setActiveModal(modal: null | AvailableModals) {
    this.activeModal = modal
  }

  toggleActiveModal(modal: AvailableModals) {
    this.activeModal = this.activeModal === modal ? null : modal
  }

  setFilterType(type: FilterType) {
    this.filterType = type
  }

  setSortType(type: SortType) {
    this.sortType = type
  }
}

export const TripList = observer(function TripList() {
  const rootStore = useStores()
  const store = useMemo(() => new TripListStore(), [])

  const { data: userTrips, isLoading } = useQuery({
    queryKey: userTripsQueryKey(rootStore?.loggedInUser?.id),
    queryFn: () => getUserTrips(rootStore?.loggedInUser?.id, rootStore?.loggedInUser?.isSuperAdmin),
    enabled: rootStore?.loggedInUser?.id && rootStore?.loggedInUser?.id !== "",
  })

  const queryClient = useQueryClient()

  const invalidateUserTrips = () => {
    queryClient
      .invalidateQueries({ queryKey: userTripsQueryKey(rootStore?.loggedInUser?.id) })
      .catch((error) => console.error("user trips query invalidation failure", error))
  }

  const { mutate: mutateCreateTrip, isPending: isCreatingTrip } = useMutation({
    mutationFn: async (values: CreateTripFormValues) => {
      rootStore.setProp("cruiseDetailsPlaceholderData", null)
      return await createTrip({
        name: values.tripName.trim(),
        originPortGroups: values.originPortGroups.map((group) => ({
          ...group,
          id: Number(group.id),
        })),
        destinationRegions: values.destinationRegions.map((region) => ({
          ...region,
          id: Number(region.id),
        })),
        months: values.months.map((e) => e.getTime()),
        loggedInUser: rootStore.loggedInUser,
      })
    },
    onSuccess: async (createdTrip) => {
      if (!createdTrip) return
      rootStore.setProp("activeTrip", createdTrip)
      invalidateUserTrips()
      store.setActiveModal(null)
    },
  })

  const navigatorStates = rootStore.navigatorStates

  useEffect(() => {
    if (!rootStore.navigatorStates || !userTrips) return

    if (rootStore.navigatorStates.size !== userTrips.length) {
      queryClient
        .invalidateQueries({ queryKey: userTripsQueryKey(rootStore?.loggedInUser?.id) })
        .catch((error) => console.error("user trips query invalidation failure", error))
    }
  }, [navigatorStates?.size])

  const filteredTrips = filterTrips(
    userTrips,
    store.filterType,
    store.sortType,
    store.searchInput,
    navigatorStates as Map<string, TripNavigatorState>,
  )
  const filteredTripsCount = filteredTrips?.length || 0

  const totalTripsCount = userTrips?.length || 0
  const archivedTripsCount =
    userTrips?.filter((trip) => trip.tags?.includes("archived")).length || 0

  const debouncedSearch = debounce((value: string) => store.setSearchInput(value), 300)

  if (!rootStore?.loggedInUser?.id) return null

  const { activeTrip, loggedInUser } = rootStore

  const userName = `${loggedInUser.firstName} ${loggedInUser.lastName}`

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <h1 className={styles.title} data-testid="trips-text">
            Trips
          </h1>
          <button
            className={styles.button}
            onClick={() => store.toggleActiveModal("createTrip")}
            data-testid="create-trip-button"
          >
            <PlusOutline className={styles.icon} />
            <p>New trip</p>
          </button>
        </div>
        <div className={styles.search}>
          <SearchOutline className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search"
            onChange={(e) => debouncedSearch(e.target.value)}
            data-testid="search-input"
          />
          {store.searchInput.length > 0 ? (
            <button
              className={styles.clearButton}
              onClick={() => {
                store.setSearchInput("")
                const input = document.querySelector(`.${styles.searchInput}`) as HTMLInputElement
                if (input) input.value = ""
              }}
              data-testid="clear-search-button"
            >
              <CloseOutline className={styles.icon} />
            </button>
          ) : null}
        </div>
        <div className={styles.headerRow}>
          <Popover
            align="start"
            positions={["bottom"]}
            isOpen={store.activeModal === "filteringModal"}
            onClickOutside={() => store.setActiveModal(null)}
            wrapperClassName={styles.filteringModal}
            content={
              <FilterSortDropdown
                options={[
                  {
                    id: "open",
                    label: `Open (${totalTripsCount - archivedTripsCount})`,
                    testId: "trip-list-filtering-open",
                  },
                  {
                    id: "archived",
                    label: `Archived (${archivedTripsCount})`,
                    testId: "trip-list-filtering-archived",
                  },
                  {
                    id: "all",
                    label: `All (${totalTripsCount})`,
                    testId: "trip-list-filtering-all",
                  },
                ]}
                onClick={(option) => {
                  store.setFilterType(option.id as FilterType)
                  store.setActiveModal(null)
                }}
              />
            }
          >
            <button
              className={classNames(
                styles.filteringContainer,
                store.activeModal === "filteringModal" && styles.filteringContainerActive,
              )}
              onClick={() => store.toggleActiveModal("filteringModal")}
              data-testid="trip-list-filtering-button"
            >
              <p>
                {store.filterType === "open"
                  ? "Open"
                  : store.filterType === "archived"
                    ? "Archived"
                    : "All"}
                {` (${filteredTripsCount})`}
              </p>
              {store.activeModal === "filteringModal" ? (
                <ChevronUpOutline className={styles.icon} />
              ) : (
                <ChevronDownOutline className={styles.icon} />
              )}
            </button>
          </Popover>
          <Popover
            align="start"
            positions={["bottom"]}
            isOpen={store.activeModal === "sortingModal"}
            onClickOutside={() => store.setActiveModal(null)}
            wrapperClassName={styles.sortingModal}
            content={
              <FilterSortDropdown
                options={[
                  {
                    id: "priority",
                    label: "Priority first",
                    testId: "trip-list-sorting-priority-first",
                  },
                  { id: "date", label: "Date", testId: "trip-list-sorting-date" },
                ]}
                onClick={(option) => {
                  store.setSortType(option.id as "priority" | "date")
                  store.setActiveModal(null)
                }}
              />
            }
          >
            <button
              className={classNames(
                styles.sortingContainer,
                store.activeModal === "sortingModal" && styles.sortingContainerActive,
              )}
              onClick={() => store.toggleActiveModal("sortingModal")}
              data-testid="trip-list-sorting-button"
            >
              <p>{store.sortType === "priority" ? "Priority first" : "Date"}</p>
              {store.activeModal === "sortingModal" ? (
                <ChevronUpOutline className={styles.icon} />
              ) : (
                <ChevronDownOutline className={styles.icon} />
              )}
            </button>
          </Popover>
        </div>
      </div>
      <div className={styles.tripListContainer}>
        {isLoading ? (
          <div className={styles.loading}>
            <DefaultSpinner />
          </div>
        ) : (
          <div className={styles.tripList}>
            {filteredTripsCount > 0 ? (
              filteredTrips.map((trip, index) => (
                <TripCard
                  key={trip.id}
                  name={trip.name}
                  tripDetails={getTripDetails(
                    trip.months,
                    trip.collaborators,
                    trip?.bookedCruiseDetails,
                  )}
                  id={trip.id}
                  invalidateUserTrips={invalidateUserTrips}
                  isSelected={activeTrip && activeTrip.id === trip.id}
                  onClick={() => {
                    if (!activeTrip || activeTrip.id !== trip.id) {
                      rootStore.setProp("cruiseDetailsPlaceholderData", null)
                      rootStore.setProp("paymentRequest", null)
                      rootStore.setProp("activeTrip", trip)
                    }
                  }}
                  testId={`trip-card-${index + 1}`}
                />
              ))
            ) : (
              <p className={styles.errorText}>No trips found</p>
            )}
          </div>
        )}
      </div>
      <button
        className={classNames(
          styles.loggedInUserRow,
          store.activeModal === "accountModal" && styles.loggedInUserRowActive,
        )}
        data-testid="account-button"
        onClick={() => store.toggleActiveModal("accountModal")}
      >
        <UserAvatar userId={loggedInUser.id} size="sm" />
        <div className={styles.userDetails}>
          <div className={styles.userInfo}>
            <p className={styles.userName}>
              {userName.length > 18 ? `${userName.slice(0, 18)}...` : userName}
            </p>
          </div>
          <p className={styles.userEmail}>
            {loggedInUser.email.length > 27
              ? `${loggedInUser.email.slice(0, 27)}...`
              : loggedInUser.email}
          </p>
        </div>
      </button>
      {store.activeModal === "accountModal" ? (
        <ModalWrapper>
          <AccountModal
            closeModal={() => store.setActiveModal(null)}
            openSubscriptionModal={() => store.setActiveModal("subscriptionModal")}
          />
        </ModalWrapper>
      ) : null}
      {store.activeModal === "subscriptionModal" ? (
        <ModalWrapper>
          <SubscriptionModal onClose={() => store.setActiveModal(null)} />
        </ModalWrapper>
      ) : null}
      {store.activeModal === "createTrip" ? (
        <ModalWrapper>
          <TripDetailsModal
            closeModal={() => store.setActiveModal(null)}
            onSubmit={(values) => mutateCreateTrip(values)}
            isLoading={isCreatingTrip}
          />
        </ModalWrapper>
      ) : null}
    </div>
  )
})
